import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalVariant } from '@patternfly/react-core';
import {
  AiAssistSidebar as UiAiAssistSidebar,
  AiAssistRelatedItem,
  AiAssistTask,
  useFeature,
} from '@ole-ui/ole-ui-components';
import useMobxStores from 'hooks/useMobxStores';
import { EnablementFeatureFlags } from 'config/featureFlags';
import type AIAssistantCourseStore from 'components/Course/AiAssist/store';

type AiAssistSidebarProps = React.ComponentProps<typeof UiAiAssistSidebar> & {
  commentSent?: boolean;
  showDisclaimer?: boolean;
  disclaimerAccepted?: boolean;
  handleAcceptDisclaimer?: () => void;
  disclaimerText?: string;
  showViewRecommendationsButton?: boolean;
};

const AiAssistSidebarWithCommentSent =
  UiAiAssistSidebar as React.ComponentType<AiAssistSidebarProps>;

const getAppBasePath = () => {
  if (typeof window === 'undefined') return '/rol/app';
  const p = window.location?.pathname || '';
  const idx = p.indexOf('/courses/');
  return idx >= 0 ? p.slice(0, idx) : '/rol/app';
};

const skuToCoursePageHref = (sku: string) => {
  const parts = sku.split('/').filter(Boolean);
  if (parts.length < 3) return null;
  const courseCode = parts[0];
  const version = parts[1];
  const page = parts.slice(2).join('/');
  if (!courseCode || !version || !page) return null;
  return `${getAppBasePath()}/courses/${courseCode}-${version}/pages/${page}`;
};

const trimSurveyUrl = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  return raw.trim();
};

/**
 * Returns the Luna survey URL for the given Rhu and RHLS configs.
 */
const lunaSurveysHref = (isRhu: boolean, rhuRaw: unknown, rhlsRaw: unknown) =>
  isRhu
    ? trimSurveyUrl(rhuRaw) ?? trimSurveyUrl(rhlsRaw)
    : trimSurveyUrl(rhlsRaw);

/**
 * Appends Luna survey analytics params; preserves existing query string on the config URL.
 */
function appendLunaSurveyQueryParams(
  href: string,
  options: { isTestData: boolean },
): string {
  try {
    const url = new URL(href);
    url.searchParams.set('is_test_data', options.isTestData ? 'yes' : 'no');
    return url.toString();
  } catch {
    return href;
  }
}

type Props = {
  aiAssistantStore: AIAssistantCourseStore;
};

const AiAssistSidebar = ({ aiAssistantStore }: Props) => {
  const { t } = useTranslation();
  const { userStore, configStore } = useMobxStores();

  const disclaimerAccepted = Boolean(
    userStore.userSettings?.ai_assistant_disclaimer_accepted,
  );

  const isAiAssistElaborateEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.AIAssistantElaborateEnabled,
    defaultFlagValue: false,
  });

  useEffect(() => {
    if (userStore.isLoggedIn && !userStore.didFetchUserSettings) {
      userStore.getUserIdSettings().catch(() => {});
    }
  }, [userStore.isLoggedIn, userStore.didFetchUserSettings]);

  const {
    isLoading,
    currentTask,
    message,
    outputHtml,
    related,
    clear: onClear,
    rating,
    submitRating: onRating,
    comment,
    setComment: onCommentChange,
    commentSent,
    pendingRecommendationText,
    setPendingRecommendationText,
    setIsOpen,
    setSelectedText,
    pendingSummaryText,
    pendingElaborationText,
    setPendingElaborationText,
    setPendingSummaryText,
    requestRecommendations,
    requestSummary,
    requestElaboration,
  } = aiAssistantStore;

  const lunaSurveyConfig = configStore.lunaSurveys;
  const summarizationSurveyHref = lunaSurveysHref(
    userStore.isRHUSubscriber,
    lunaSurveyConfig.rhu_summarization,
    lunaSurveyConfig.summarization,
  );
  const recommendationsSurveyHref = lunaSurveysHref(
    userStore.isRHUSubscriber,
    lunaSurveyConfig.rhu_recommendations,
    lunaSurveyConfig.recommendations,
  );

  const recommendationsSurveyCompleted = Boolean(
    userStore.userSettings?.ai_assistant_recommendations_survey_completed,
  );
  const summarizationSurveyCompleted = Boolean(
    userStore.userSettings?.ai_assistant_summarization_survey_completed,
  );

  const [surveyModalKind, setSurveyModalKind] = useState<
    'recommendations' | 'summarization' | null
  >(null);

  const runSurveyContinue = useCallback(
    (kind: 'recommendations' | 'summarization') => {
      const isTestData =
        configStore.rhtDeployment !== 'prod' &&
        configStore.rhtDeployment !== 'rhu';

      if (kind === 'recommendations') {
        if (userStore.isLoggedIn) {
          userStore
            .updateUserSettings({
              ai_assistant_recommendations_survey_completed: true,
            })
            .catch(() => {});
        }
        if (recommendationsSurveyHref) {
          const href = appendLunaSurveyQueryParams(recommendationsSurveyHref, {
            isTestData,
          });
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      } else {
        if (userStore.isLoggedIn) {
          userStore
            .updateUserSettings({
              ai_assistant_summarization_survey_completed: true,
            })
            .catch(() => {});
        }
        if (summarizationSurveyHref) {
          const href = appendLunaSurveyQueryParams(summarizationSurveyHref, {
            isTestData,
          });
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
    },
    [
      configStore,
      userStore,
      recommendationsSurveyHref,
      summarizationSurveyHref,
    ],
  );

  const handleSurveyModalContinue = () => {
    if (surveyModalKind) {
      runSurveyContinue(surveyModalKind);
    }
    setSurveyModalKind(null);
  };

  const relatedWithHref = useMemo<AiAssistRelatedItem[]>(() => {
    if (!Array.isArray(related)) return [];
    return related.map((r) => {
      const anyR = r as any;
      const sku = (anyR.slug as string) || (anyR.id as string) || '';
      const href =
        (anyR.href as string) || (sku ? skuToCoursePageHref(sku) : null);
      return { ...(anyR as AiAssistRelatedItem), href: href || undefined };
    });
  }, [related]);

  const handleAcceptDisclaimer = () => {
    userStore
      .updateUserSettings({ ai_assistant_disclaimer_accepted: true })
      .then(() => {
        if (pendingRecommendationText?.trim()) {
          requestRecommendations(pendingRecommendationText);
          setPendingRecommendationText(null);
        }
        if (pendingSummaryText?.trim()) {
          requestSummary(pendingSummaryText);
          setPendingSummaryText(null);
        }
        if (pendingElaborationText?.trim()) {
          requestElaboration(pendingElaborationText);
          setPendingElaborationText(null);
        }
      })
      .catch(() => {});
  };

  const showDisclaimer = userStore.isLoggedIn && !disclaimerAccepted;
  const disclaimerText = t(
    'Learning Assistant disclaimer',
    'You are about to use a Red Hat tool that utilizes AI technology to provide you with information. Responses provided by tools utilizing AI technology should be reviewed and verified prior to use.',
  );

  const feedbackRecommendationsLabel = t(
    'Learning Assistant feedback recommendations',
    'Take recommendation survey',
  );
  const feedbackSummarizationLabel = t(
    'Learning Assistant feedback summarization',
    'Take summarization survey',
  );

  const surveyConfirmTitle = t(
    'Learning Assistant survey confirm title',
    'Before you continue',
  );
  const surveyConfirmBody = t(
    'Learning Assistant survey confirm body',
    'This survey is intended to be completed once. If you continue, the survey button will no longer appear.',
  );
  const surveyConfirmContinue = t('Continue');
  const surveyConfirmCancel = t('Cancel');

  return (
    <>
      <Modal
        variant={ModalVariant.small}
        title={surveyConfirmTitle}
        titleIconVariant="info"
        isOpen={surveyModalKind !== null}
        onClose={() => setSurveyModalKind(null)}
        showClose={false}
        data-analytics-id="hud-ai-assist-survey-modal-ole-lp"
        actions={[
          <Button
            key="continue"
            variant="primary"
            onClick={handleSurveyModalContinue}
            data-analytics-id="hud-ai-assist-survey-continue-button-ole-lp"
          >
            {surveyConfirmContinue}
          </Button>,
          <Button
            key="cancel"
            variant="link"
            onClick={() => setSurveyModalKind(null)}
            data-analytics-id="hud-ai-assist-survey-cancel-button-ole-lp"
          >
            {surveyConfirmCancel}
          </Button>,
        ]}
      >
        {surveyConfirmBody}
      </Modal>
      <AiAssistSidebarWithCommentSent
        belowRecommendations={
          <>
            {currentTask === 'suggest' &&
              recommendationsSurveyHref &&
              !recommendationsSurveyCompleted && (
                <Button
                  className="hud-course-view__ai-assist-feedback-survey-button"
                  variant="primary"
                  isBlock
                  onClick={() => setSurveyModalKind('recommendations')}
                  data-analytics-id="hud-ai-assist-recommendations-survey-button-ole-lp"
                >
                  {feedbackRecommendationsLabel}
                </Button>
              )}
            {currentTask === 'summarize' &&
              summarizationSurveyHref &&
              !summarizationSurveyCompleted && (
                <Button
                  className="hud-course-view__ai-assist-feedback-survey-button"
                  variant="primary"
                  isBlock
                  onClick={() => setSurveyModalKind('summarization')}
                  data-analytics-id="hud-ai-assist-summarization-survey-button-ole-lp"
                >
                  {feedbackSummarizationLabel}
                </Button>
              )}
          </>
        }
        comment={comment}
        commentSent={commentSent}
        disclaimerAccepted={disclaimerAccepted}
        disclaimerText={disclaimerText}
        handleAcceptDisclaimer={handleAcceptDisclaimer}
        isLoading={isLoading}
        message={message}
        onClear={onClear}
        onCommentChange={onCommentChange}
        onRating={onRating}
        onFullPageElaborate={
          isAiAssistElaborateEnabled
            ? () => {
                setSelectedText('Full page elaboration');
                if (!disclaimerAccepted) {
                  setPendingElaborationText('Full page elaboration');
                  setIsOpen(true);
                } else {
                  requestElaboration('Full page elaboration', true);
                }
              }
            : undefined
        }
        onFullPageRecommend={() => {
          setSelectedText('Full page recommendations');
          if (!disclaimerAccepted) {
            setPendingRecommendationText('Full page recommendations');
            setIsOpen(true);
          } else {
            requestRecommendations('Full page recommendations', true);
          }
        }}
        onFullPageSummarize={() => {
          setSelectedText('Full page summary');
          if (!disclaimerAccepted) {
            setPendingSummaryText('Full page summary');
            setIsOpen(true);
          } else {
            requestSummary('Full page summary', true);
          }
        }}
        outputHtml={outputHtml}
        rating={rating}
        related={relatedWithHref}
        showDisclaimer={showDisclaimer}
        showViewRecommendationsButton={false}
        task={currentTask as AiAssistTask}
      />
    </>
  );
};

export default observer(AiAssistSidebar);
